import React from 'react';
import { styled } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
} from '@mui/material';
import HouseOutlinedIcon from '@mui/icons-material/HouseOutlined';
import TripOriginOutlinedIcon from '@mui/icons-material/TripOriginOutlined';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import EmojiTransportationIcon from '@mui/icons-material/EmojiTransportation';
import PersonIcon from '@mui/icons-material/Person';
import BackHandOutlinedIcon from '@mui/icons-material/BackHandOutlined';
import {
  HelpOutlineOutlined,
  Person2Outlined,
  ReceiptLongOutlined,
} from '@mui/icons-material';
import CommuteOutlinedIcon from '@mui/icons-material/CommuteOutlined';
import { useCommonTranslation } from '../../hooks/useCommonTranslation';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { useLoggedInProviderId } from '../../hooks/useLoggedInProviderId';
import { useIsF1Provider } from '../../hooks/permissions/useIsF1Provider';
import { useIsPRUser } from '../../hooks/permissions/useIsPRUser';
import * as providerSlice from '../../data/provider.slice';

const PREFIX = 'HomeNavBar';

const classes = {
  content: `${PREFIX}-content`,
  icon: `${PREFIX}-icon`,
  unselected: `${PREFIX}-unselected`,
  border: `${PREFIX}-border`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`&.${classes.content}`]: {
    width: '248px',
    borderRadius: 0,
    borderRight: '1px solid rgba(0, 0, 0, 0.15)',
  },
  [`& .${classes.icon}`]: {
    minWidth: 36,
    width: 40,
    justifyContent: 'center',
  },
  [`& .${classes.unselected}`]: {
    opacity: 0.75,
  },
  [`& .${classes.border}`]: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: '#1B282A',
    borderRadius: 10,
    width: 5,
  },
  [`& .MuiListItem-root`]: {
    position: 'relative',
  },
}));

export default function HomeNavBar() {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const isProvider = !!useLoggedInProviderId();
  const isF1Provider = useIsF1Provider();
  const isPRUser = useIsPRUser();
  const providerID = useLoggedInProviderId();
  const provider = useAppSelector(providerSlice.selectProvider);
  const [providerName, setProviderName] = React.useState('');

  React.useEffect(() => {
    if (isProvider && providerID) {
      dispatch(providerSlice.getProviderAsync(providerID));
    }
  }, []);

  React.useEffect(() => {
    if (provider) {
      setProviderName(provider.transportationProviderName ?? '');
    }
  }, [provider]);

  const navItems = [
    {
      isProvider: true,
      url: '/home',
      text: isPRUser ? 'Benson' : t('common.home'), // Benson is temporary so I'm not putting it in resource file
      icon: <HouseOutlinedIcon className={classes.icon} />,
    },
    {
      isProvider: true,
      url: '/providers',
      text: t('common.profile'),
      icon: <Person2Outlined className={classes.icon} />,
    },
    {
      isProvider: true,
      url: '/trips',
      text: t('common.trips'),
      icon: <CommuteOutlinedIcon className={classes.icon} />,
    },
    {
      isProvider: true,
      url: '/invoices',
      text: t('common.invoices'),
      icon: <ReceiptLongOutlined className={classes.icon} />,
    },
    {
      isProvider: false,
      url: '/providers',
      text: t('common.providers'),
      icon: <EmojiTransportationIcon className={classes.icon} />,
    },
    {
      isProvider: false,
      url: '/drivers',
      text: t('common.drivers'),
      icon: <PersonIcon className={classes.icon} />,
    },
    {
      isProvider: false,
      url: '/vehicles',
      text: t('common.vehicles'),
      icon: <AirportShuttleIcon className={classes.icon} />,
    },
  ];

  if (isF1Provider) {
    navItems.splice(1, 0, {
      isProvider: true,
      url: '/tripPackages',
      text: t('common.tripPackages'),
      icon: <TripOriginOutlinedIcon className={classes.icon} />,
    });
  }

  if (isPRUser) {
    navItems.splice(
      5,
      0,
      {
        isProvider: false,
        url: '/benson',
        text: 'Benson', // this is temporary so I'm not bothering to put it into resource string file
        icon: <TripOriginOutlinedIcon className={classes.icon} />,
      },
      {
        isProvider: false,
        url: '/invoices',
        text: t('common.invoices'),
        icon: <ReceiptLongOutlined className={classes.icon} />,
      },
    );
  }

  const isCurrentRoute = (path: string, url: string): boolean => {
    return path.includes(url);
  };

  return (
    <StyledPaper className={classes.content} elevation={2}>
      {isProvider && (
        <Box
          sx={{
            padding: 1,
            display: 'flex',
            justifyContent: 'center',
          }}>
          <Typography variant="subtitle1">{providerName}</Typography>
        </Box>
      )}
      <List>
        {navItems
          .filter((item) => item.isProvider === isProvider)
          .map((item) => {
            const current = isCurrentRoute(location.pathname, item.url);
            return (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  className={current ? '' : classes.unselected}
                  onClick={() => navigate(item.url)}>
                  <ListItemIcon className={classes.icon}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
                {current && <Box className={classes.border} />}
              </ListItem>
            );
          })}

        <ListItem key={'Help'} disablePadding>
          <ListItemButton
            target="_blank"
            href="https://americanlogistics.com/ridecafe-faqs/#home"
            className={classes.unselected}>
            <ListItemIcon className={classes.icon}>
              <HelpOutlineOutlined />
            </ListItemIcon>
            <ListItemText primary={t('common.help')} />
          </ListItemButton>
        </ListItem>
      </List>
    </StyledPaper>
  );
}
